/**
 * 购物车「形状版本」—— 上线新版本时，让**存量的持久化购物车整个作废**。
 *
 * ## 为什么需要
 *
 * `src/store/index.js` 的 `VuexPersistence` 没配 `reducer`，**整个 root state**
 * （含 `productList` 购物车）落 localStorage 并**跨版本保留**。顾客手机里那份
 * 购物车可能是任意老版本写下的，新代码却按新形状去读它。
 *
 * 触发本次改动的那条：套餐子品的「哪一格」是新造的键 `_comboSlot`，只存在于上线
 * **之后**新建的行。上线之前建的行两头都没有这个键 —— 而且那些行**本身就已经是错的**，
 * 修不回来。用 `master` 的代码真跑一遍就能看到（`_getComboProductList` 建组 →
 * 顾客逐格配菜 → `_saveComboProductList` 结账）：
 *
 *   一个 `oneProduct` 组里 Wings x2（份数靠重复条目承载），顾客第 1 格点 Tomato、
 *   第 2 格点 Corn，master 存下来的那一行是
 *     屏幕（`options[].detail`）= [["Corn"], []]      ← 两次点击都落在第 0 格
 *     记录（`comboProductList`）= [["Corn"], []]
 *     进 POS（`addList`）       = [["Corn"], ["Corn"]]
 *
 * 也就是说 master **只写过 id 首次命中的那一条**，同 id 的其余记录一个都没勾、
 * 仍是菜单默认值。「第 k 条记录 = 第 k 格的真相」这个假设不成立，任何按位置的回填
 * 都会把**空记录**当成顾客的选择送进 POS —— 必选配菜静默消失，而
 * `verificationComboRequired` 只遍历 `requiredComboList`（`oneProduct` 组），
 * `type: 'required'` 的组根本不在门内，闸门也拦不住。
 *
 * 所以 owner 2026-08-11 裁定：**不猜、不回填，上线时把存量购物车整个清掉**。
 *
 * ## 怎么做到「不是先恢复再清」
 *
 * 判定必须发生在 **restore 那一刻**：本模块的 `restoreCartAwareState` 是 vuex-persist
 * 的 `restoreState` 钩子，在 `store.replaceState(merge(store.state, savedState))`
 * **之前**就把购物车那几个键从 savedState 里删掉。删掉之后 `merge` 拿到的是
 * `store.state` 自己的初值（`productList: []` / `subGroupCartList: {}`），
 * 于是**过期的购物车一帧都没有在 store 里存在过**。
 * 如果写成「恢复完再 commit 一个清空」，中间那一帧顾客可能已经点了结算。
 *
 * ## 清空之后不会把应用打崩
 *
 * **被清掉的那几个键**与「第一次访问的顾客」逐个相同 —— 第一次访问没有 localStorage，
 * `savedState` 是 `{}`，这几个键同样取 `initState` 的初值。空购物车是应用每天的
 * 启动状态，不是新形状。
 *
 * ⚠️ 只能说到这一步：**整份 state 与首访并不相同**，也不该相同 —— 语言、venue、
 * 联系人、未付款订单号全都刻意留着。把结论写成「整个 state 与首访逐字节相同」，
 * 对应的用例只要喂进去的 blob 里除了购物车什么都没有，就**对任何实现都失败不了**。
 */

/**
 * ⚠️ **必须是常量**。写成时间戳 / 随机数的话每次刷新都对不上，顾客的购物车会被
 * 无限清空、一件都加不进去。
 *
 * 只在「新代码读不懂旧购物车行」时 +1，并在 changelog 的运维提醒里写明
 * 「上线后顾客的购物车会清空一次」。
 */
export const CART_SCHEMA_VERSION = 1

/**
 * 版本号存进持久化下来的这个键。上线之前写下的 localStorage 里**根本没有它**，
 * 于是一定对不上、一定作废 —— 这正是本次要的效果。
 */
export const CART_SCHEMA_VERSION_KEY = '__cartSchemaVersion'

/**
 * root state 上「跟着购物车走」的键。**清车就得连着这些一起清**。
 *
 * ⚠️ 这条口径的准确说法（早先写成「`clearProductList` 的**每一处**调用点都紧挨着
 * `clearDiscounts`」，**那是错的**）：仓里绝大多数清车点确实两个一起提交
 * （`views/cart/index.vue:1651`/`:2153`、`contact-details/index.vue:857`/`:867`、
 * `payment/index.vue:2029`、`tab-user/pay/index.vue:127`、`tab-user/start-tab/index.vue:1055`、
 * `card-detail/index.vue:1295`、`venue-sub-group/cart/index.vue:1627`（2026-09-28 附加费 review 第 1 轮随死方法一起删掉，仅存史）、
 * `venue-sub-group/posPayment/index.vue:687`（该页已随 ledger-080 退役删除，
 * 此处仅存史）、`views/pay/index.vue:864`、
 * `venue-sub-group/orderDetail/index.vue:889`），但其中最后四处**是本批才补上的** ——
 * 在此之前它们只清车不清折扣，根因不是本模块造出来的，也不是本模块独有的。
 * 剩下唯一不配对的是 `views/pay/index.vue` 里 `VUE_APP_ENV !== 'production'` 的 demo
 * 重置块（两处），刻意不动。`tests/unit/store/cartSchemaVersion.spec.js` 的第 ⑥ 组
 * 把这份清单钉住：再出现新的不配对调用点会红。
 *
 * `clearProductList` 自己还会把 `orderSeed` / `currentVenueId` 置空，以及多版本菜单 B6 的 `menuId` / `cartMenuId`。
 *
 * 只清 `productList` 造出的状态是：车是空的、折扣还在。
 * 费用链 `getters._orderFeeChain` 拿 `total` 直接减 `state.discountAmount`（不设下限，与热修前的
 * `_beforeDiscountTotal` 同义；那个名不副实的 getter 2026-09-28 附加费 review 第 1 轮删掉），于是空车能算出**负数**总价、
 * 残留折扣也会从下一车里扣掉（2026-09-28 附加费热修曾给「小计 − 券」设下限 0，2026-09-29 Option A 收口随券的计算一起退回热修前）；`App.vue:550` 那道 24 小时
 * 清理在窗口内不生效。**这个状态本来就到得了**（手动 `deleteGoods` 删空、以及上面
 * 那四处清车点），本批只是不让上线清空再造出一个新实例。上线当天
 * 「24 小时内用过折扣码」的顾客会**同时**进这个状态，这是被清空显著放大的面。
 *
 * 下面每个键 `delete` 之后落到的 `initState` 初值，与 `clearDiscounts` /
 * `clearProductList` 写进去的值**逐个相同**（0 / [] / '' / null / null / null / '' / ''），
 * 所以清完的车 = 仓里既有的「刚下完单」状态，不是新形状。
 *
 * - `productList`：购物车本体
 * - `expireProductList`：从购物车里挑出来的失效商品（纯粹是上面那份的派生物）
 * - `discountAmount` / `spDiscounts` / `spDiscountsCode` / `discountRuleTime`：
 *   会员优惠，`clearDiscounts` 清的正是这四个（2026-09-28 附加费热修 Codex 第 2 轮曾加过第五个 `discountWholeOrder`，
 *   2026-09-29 Option A 收口随券的计算退回热修前时删掉；那一版没上线过，顾客存档里不会有它 —— 就算有，
 *   也只是 merge 进 root state 的一个没人读的键，不影响购物车，所以 `CART_SCHEMA_VERSION` 不用动）
 * - `orderSeed` / `currentVenueId`：A/B 测试的 per-order seed，`clearProductList` 自己会置空
 * - `menuId` / `cartMenuId`（多版本菜单 B6，2026-10-16）：这家店此刻的菜单（按 seed 解析，跟着 seed 走）与购物车行
 *   建在哪份菜单下（跟着车走），`clearProductList` 与 seed 一起置空。新增键本身不升 `CART_SCHEMA_VERSION`：
 *   老存档里没有它们 = 初值 ''，与「还没解析过菜单」相同。只存菜单 id，从不存预览码（D80）。
 *   多版本菜单 N35-B2「这一次光顾扫的是哪种码」与 N35-B5 点单链接码**都不**在 root state 里（vuex 整份落 localStorage，别的标签页会写进来）：
 *   它们只在地址上（进店参数 `mmEntry`、`menuLink`，`utils/menu/qrEntry.js` / `menuLink.js`，零存储），不随车清 —— 下完一单地址还在、还是同一张码。
 */
export const ROOT_CART_KEYS = [
  'productList',
  'expireProductList',
  'discountAmount',
  'spDiscounts',
  'spDiscountsCode',
  'discountRuleTime',
  'orderSeed',
  'currentVenueId',
  'menuId',
  'cartMenuId'
]

/**
 * `venueHome` 模块里的那份平行购物车。
 * - `subGroupCartList[venueId].cart`：子组每家店各一份购物车，`replaceCurrentCart`
 *   会把它整个搬进 `productList` —— 不一起清掉的话，过期的行下一秒就回来了。
 * - `cartAddProduct`：`addNewProductToCart` 会把它直接 concat 进 `productList`。
 */
export const VENUE_HOME_CART_KEYS = ['subGroupCartList', 'cartAddProduct']

/**
 * 把过期的购物车从「刚从 localStorage 读出来、还没进 store」的那份 state 里删掉。
 *
 * ⚠️ 只动购物车、以及仓里既有的「清车就一起清」的那几个键（折扣 / seed，见
 * `ROOT_CART_KEYS`）。语言、venue 选择、联系人、已下单未付款的订单号、
 * 各种一次性弹窗标记**一个都不碰** —— 顾客不该因为一次改版重新选一遍语言。
 *
 * @param savedState {Object} `JSON.parse(localStorage['vuex'])` 的结果（原地修改并返回）
 * @return {Object} 同一个对象；版本对得上就原样返回
 */
export function dropOutdatedCart(savedState) {
  // 不是对象就交给上层原样处理（vuex-persist 自己也接受 `{}` / 非字符串值）。
  if (!savedState || typeof savedState !== 'object' || Array.isArray(savedState)) return savedState
  if (savedState[CART_SCHEMA_VERSION_KEY] === CART_SCHEMA_VERSION) return savedState

  ROOT_CART_KEYS.forEach(key => {
    // 一定要 `delete`，别改成赋 `[]`。`delete` 的语义是「这份 blob 对这个键没有意见」，
    // 于是 `merge(store.state, savedState)` 直接落到 store 自己的初值上 —— 这个结论
    // 不依赖 lodash 的任何细节。赋值就得指望 `lodash.merge` 会用右边覆盖左边，而它**不会**：
    //   merge({}, { p: [a, b] }, { p: [] })  =>  { p: [a, b] }   （实测）
    // 今天赋 `[]` 之所以看着也对，只是因为 store 的初值本来就是空的 —— 那是巧合，不是保证。
    delete savedState[key]
  })
  const venueHome = savedState.venueHome
  if (venueHome && typeof venueHome === 'object') {
    VENUE_HOME_CART_KEYS.forEach(key => {
      delete venueHome[key]
    })
  }

  // 盖上当前版本号，否则下一次刷新会再清一次（下一版 +1 时这一条尤其重要）。
  savedState[CART_SCHEMA_VERSION_KEY] = CART_SCHEMA_VERSION
  return savedState
}

/**
 * vuex-persist 的 `restoreState` 钩子。
 *
 * 读 / 解析这两步与 vuex-persist 自带的同步实现**逐字一致**（`dist/cjs/index.js`
 * 的 sync `restoreState`），刻意不加 `try/catch` —— 那会顺手改掉「localStorage
 * 里是坏 JSON 时当场抛」的既有行为，不在本次范围内。
 *
 * @param key {String} localStorage 的键（默认 `'vuex'`）
 * @param storage {Storage} `window.localStorage`
 * @return {Object} 已经剔掉过期购物车的那份 state
 */
export function restoreCartAwareState(key, storage) {
  const value = storage.getItem(key)
  const savedState = typeof value === 'string' ? JSON.parse(value || '{}') : (value || {})
  return dropOutdatedCart(savedState)
}
